import { ArrowRight, Gift, Lock, Mail, Phone, UserRound } from 'lucide-react';
import * as React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Field, Input } from '@/components/ui/input';
import { authApi } from '@/lib/api/endpoints';
import { isApiError, userMessage } from '@/lib/api/errors';
import { useAuth } from '@/lib/auth';
import { AuthLayout } from './AuthLayout';

type Form = { email: string; password: string; full_name: string; phone: string };

/** Same rules as the API, so most mistakes show before submitting. */
function validate(f: Form): Partial<Record<keyof Form, string>> {
  const e: Partial<Record<keyof Form, string>> = {};
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email)) e.email = 'Enter a valid email address.';
  if (f.password.length < 8 || f.password.length > 128) e.password = 'At least 8 characters.';
  if (f.full_name.trim().length < 2 || f.full_name.length > 128) e.full_name = 'Enter your full name.';
  if (!/^\+?\d{7,15}$/.test(f.phone.replace(/[\s-]/g, ''))) e.phone = 'Enter 7 to 15 digits, with optional leading +.';
  return e;
}

export function RegisterPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = React.useState<Form>({ email: '', password: '', full_name: '', phone: '' });
  const [errors, setErrors] = React.useState<Partial<Record<keyof Form, string>>>({});
  const [busy, setBusy] = React.useState(false);

  const set = (k: keyof Form) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
    if (errors[k]) setErrors((x) => ({ ...x, [k]: undefined }));
  };

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    const local = validate(form);
    setErrors(local);
    if (Object.keys(local).length) return;
    setBusy(true);
    try {
      const user = await authApi.register({
        email: form.email.trim().toLowerCase(),
        password: form.password,
        full_name: form.full_name.trim(),
        phone: form.phone.replace(/[\s-]/g, ''),
      });
      await login(form.email.trim().toLowerCase(), form.password);
      toast.success('Account created!', {
        description: user.coin_balance > 0 ? `You got ${user.coin_balance} free coins to start.` : undefined,
        icon: <Gift className="size-4" />,
      });
      navigate('/', { replace: true });
    } catch (err) {
      if (isApiError(err, 409)) setErrors({ email: err.message.includes('Phone') ? 'That phone number is already registered.' : 'That email is already registered.' });
      else if (isApiError(err, 422)) setErrors(err.fieldErrors);
      else toast.error(userMessage(err, 'Sign up failed.'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthLayout>
      <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-amber-400/40 bg-amber-400/10 px-3 py-1 text-xs font-semibold text-amber-700 dark:text-amber-300">
        <Gift className="size-3.5" /> Free coins on sign-up
      </div>
      <h1 className="text-3xl font-extrabold tracking-tight">Create your account</h1>
      <p className="mt-2 text-muted-foreground">It takes less than a minute.</p>

      <form onSubmit={onSubmit} className="mt-8 space-y-4" noValidate>
        <Field label="Email" htmlFor="email" error={errors.email}>
          <Input id="email" type="email" icon={<Mail />} autoComplete="email" value={form.email} onChange={set('email')} aria-invalid={!!errors.email} required autoFocus />
        </Field>
        <Field label="Password" htmlFor="password" error={errors.password} hint="At least 8 characters.">
          <Input id="password" type="password" icon={<Lock />} autoComplete="new-password" value={form.password} onChange={set('password')} aria-invalid={!!errors.password} />
        </Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Full name" htmlFor="full_name" error={errors.full_name}>
            <Input id="full_name" icon={<UserRound />} autoComplete="name" value={form.full_name} onChange={set('full_name')} aria-invalid={!!errors.full_name} required />
          </Field>
          <Field label="Phone" htmlFor="phone" error={errors.phone}>
            <Input id="phone" type="tel" icon={<Phone />} autoComplete="tel" placeholder="+251…" value={form.phone} onChange={set('phone')} aria-invalid={!!errors.phone} required />
          </Field>
        </div>
        <Button type="submit" variant="brand" size="lg" className="group mt-2 w-full" loading={busy}>
          Create account <ArrowRight />
        </Button>
      </form>

      <p className="mt-8 text-center text-sm text-muted-foreground">
        Already have an account?{' '}
        <Link to="/login" className="font-semibold text-primary-ink hover:underline">
          Sign in
        </Link>
      </p>
    </AuthLayout>
  );
}
